import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Drawer,
  Input,
  Menu,
  Modal,
  Paper,
  Popover,
  Tooltip,
  createTheme,
  mergeThemeOverrides,
  rem,
} from "@mantine/core";
import type { MantineTransition } from "@mantine/core";
import { shadcnTheme } from "../theme/theme";

const EASE = "cubic-bezier(0.4, 0, 0.2, 1)";
const NA_IN: MantineTransition = {
  in: { opacity: 1, transform: "scale(1)" },
  out: { opacity: 0, transform: "scale(0.98)" },
  transitionProperty: "transform, opacity",
};
const NA_SLIDE: MantineTransition = {
  in: { opacity: 1, transform: "translateX(0)" },
  out: { opacity: 0, transform: "translateX(24px)" },
  transitionProperty: "transform, opacity",
};

/**
 * Portal theme: the shared Null theme with the redesign's stricter rules
 * (square corners, opaque scrims, Geist). Passed only by apps/portal.
 */
export const naPortalTheme = mergeThemeOverrides(
  shadcnTheme,
  createTheme({
    fontFamily: "Geist Sans, Geist, ui-sans-serif, system-ui, sans-serif",
    fontFamilyMonospace: "Geist Mono, ui-monospace, monospace",
    headings: { fontFamily: "Geist Sans, Geist, sans-serif" },
    defaultRadius: 0,
    // Keyboard focus shows (global.css :focus-visible); mouse clicks don't.
    focusRing: "auto",
    respectReducedMotion: true,
    other: {
      motion: { fast: 150, base: 300, slow: 420, ease: "cubic-bezier(.4,0,.2,1)" },
    },
    components: {
      Button: Button.extend({ defaultProps: { radius: 0, variant: "default" } }),
      ActionIcon: ActionIcon.extend({ defaultProps: { radius: 0, variant: "subtle" } }),
      Input: Input.extend({ defaultProps: { radius: 0 } }),
      Card: Card.extend({ defaultProps: { radius: 0 } }),
      Paper: Paper.extend({ defaultProps: { radius: 0 } }),
      Badge: Badge.extend({ defaultProps: { radius: rem(2) } }),
      // Enter motion is a CSS `naIn` (global.css); Mantine's own is off.
      Tooltip: Tooltip.extend({ defaultProps: { radius: 0, openDelay: 250, transitionProps: { duration: 0 } } }),
      Popover: Popover.extend({ defaultProps: { radius: 0, shadow: "xl", transitionProps: { duration: 0 } } }),
      Menu: Menu.extend({ defaultProps: { radius: 0, shadow: "xl", transitionProps: { duration: 0 } } }),
      Modal: Modal.extend({
        defaultProps: {
          radius: 0,
          centered: true,
          // naIn: opacity + scale(.98) → 1, 160 ms
          transitionProps: { transition: NA_IN, duration: 160, timingFunction: EASE },
          overlayProps: { backgroundOpacity: 0.6, blur: 0, color: "#000" },
          closeButtonProps: { "aria-label": "Close" },
        },
      }),
      Drawer: Drawer.extend({
        defaultProps: {
          radius: 0,
          position: "right",
          size: "min(460px, 100vw)",
          // naSlide: translateX(24px) + opacity, 220 ms
          transitionProps: { transition: NA_SLIDE, duration: 220, timingFunction: EASE },
          overlayProps: { backgroundOpacity: 0.45, blur: 0, color: "#000" },
          closeButtonProps: { "aria-label": "Close" },
        },
      }),
    },
  }),
);
